import * as THREE from 'three';
import { AudioEngine } from './audio/AudioEngine';
import { CONFIG } from './config';
import { randomPost } from './content/facespace';
import { byBody } from './content/looks';
import { localHeadline, tickerItem } from './content/headlines';
import { NPCS } from './content/npcs';
import { pickShow } from './content/tv';
import type { ContentUi, GameContext, SfxName, WorldEffects } from './content/types';
import { CameraRig } from './core/CameraRig';
import { isPortrait, isTouch, onTouchChange } from './core/device';
import { Gamepads } from './core/Gamepads';
import { Input } from './core/Input';
import type { PadContext } from './core/padmap';
import { clamp, damp, formatMoney } from './core/math';
import { Random } from './core/Random';
import { NpcManager } from './entities/NpcManager';
import { Player } from './entities/Player';
import { Activities } from './game/Activities';
import { BackForty } from './game/BackForty';
import { Driving } from './game/Driving';
import { Fairgrounds } from './game/Fairgrounds';
import { Games } from './game/Games';
import { Lotto } from './game/Lotto';
import { Abduction } from './game/Abduction';
import { Rascal, RASCAL_BARKS } from './game/Rascal';
import { Hustles } from './game/Hustles';
import { Favors } from './game/Favors';
import { CarWash } from './game/CarWash';
import { Deliveries } from './game/Deliveries';
import { ShineRuns } from './game/ShineRuns';
import { Landlord } from './game/Landlord';
import { Campfires } from './game/Campfires';
import { Shops } from './game/Shops';
import { ParkHomes } from './game/ParkHomes';
import { Townsfolk } from './game/Townsfolk';
import { Strip } from './game/Strip';
import { Interiors } from './game/Interiors';
import { Neighbors } from './game/Neighbors';
import { registerInteractions } from './game/registerInteractions';
import { RestCycle } from './game/RestCycle';
import type { GameServices } from './game/services';
import { loadSettings, storeSettings } from './game/settings';
import { StuntFlow } from './game/StuntFlow';
import { trackAchievements } from './state/achievementTracker';
import { createInitialState, DEFAULT_LOOK } from './state/defaults';
import { dayName, isPassOutTime } from './state/rules';
import { loadGame, saveGame } from './state/save';
import { Store } from './state/Store';
import type { CharacterLook, ItemId } from './state/types';
import { Director } from './systems/Director';
import { Interactions } from './systems/Interactions';
import { Jobs } from './systems/Jobs';
import { StuntController } from './systems/Stunts';
import { EndScreen } from './ui/EndScreen';
import { Hud } from './ui/Hud';
import { StageHost } from './ui/StageHost';
import type { BullMount } from './stages/BullStage';
import { OnlinePanel } from './ui/OnlinePanel';
import { PadNav } from './ui/PadNav';
import { Phone, type Settings } from './ui/Phone';
import { SceneModal } from './ui/SceneModal';
import { ScratchOff } from './ui/ScratchOff';
import { ShopUI } from './ui/ShopUI';
import { StuntMeter } from './ui/StuntMeter';
import { CharacterCreator } from './ui/CharacterCreator';
import { TitleScreen } from './ui/TitleScreen';
import { TouchControls } from './ui/TouchControls';
import { TvScreen } from './ui/TvScreen';
import { DecorManager } from './world/DecorManager';
import { Effects } from './world/Effects';
import { Highlighter } from './world/Highlighter';
import { foilHatProp, Lamp, type LampKind } from './world/Lamp';
import { homeLot, homeSpots, LOT_7, type HomeFrame, type HomeKind } from './world/home';
import { trailerStyle, type HomeLot } from './world/HomeLot';
import {
  BACK_FORTY_LITTER,
  backFortyDepth,
  inFairgrounds,
  inRect,
  MUD_BOG,
  RODEO,
  SPOTS,
  type SpotId,
} from './world/layout';
import { Pickups } from './world/Pickups';
import { renderPortrait } from './world/portraits';
import { Sky } from './world/Sky';
import { World } from './world/World';

const v3 = (x: number, z: number, y = 0): THREE.Vector3 => new THREE.Vector3(x, y, z);
/** The title screen always shows Lot 7. */
const TITLE_SPOT = homeSpots(LOT_7).spawn;
/** Which way the character on the title screen faces (toward the street). */
const TITLE_FACING = Math.PI * 0.15;

/**
 * Owns the renderer, simulation and UI; composes the gameplay modules in ./game
 * and runs the main loop.
 */
export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  /** First-person props (a rifle in your hands) draw last, over everything, with their own lights. */
  private readonly viewScene = new THREE.Scene();
  private readonly viewRoot = new THREE.Group();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly cameraRig: CameraRig;
  private readonly sky: Sky;
  private readonly world: World;
  private readonly decor: DecorManager;
  /** Your home: Lot 7, unless a multiplayer park put you somewhere else. */
  private localHome: HomeLot;
  private readonly pickups: Pickups;
  private readonly effects: Effects;
  private readonly npcs: NpcManager;
  private readonly player: Player;
  private readonly store: Store;
  private readonly rng = new Random(Date.now() >>> 0);
  private readonly input: Input;
  private readonly pads: Gamepads;
  private readonly audio = new AudioEngine();
  private readonly interactions = new Interactions();
  private readonly director: Director;
  private readonly jobs: Jobs;
  private readonly stunts: StuntController;
  private readonly ctx: GameContext;
  private readonly services: GameServices;

  private readonly driving: Driving;
  private readonly rest: RestCycle;
  private readonly stuntFlow: StuntFlow;
  private readonly activities: Activities;
  private readonly backForty: BackForty;
  private readonly interiors: Interiors;
  private readonly games: Games;
  private readonly strip: Strip;
  private readonly shops: Shops;
  private readonly townsfolk: Townsfolk;
  private readonly parkHomes: ParkHomes;
  private readonly campfires: Campfires;
  private readonly abduction: Abduction;
  private readonly rascal: Rascal;
  private readonly hustles: Hustles;
  private readonly favors: Favors;
  private readonly carWash: CarWash;
  private readonly deliveries: Deliveries;
  private readonly shineRuns: ShineRuns;
  private readonly landlord: Landlord;
  private readonly fairgrounds: Fairgrounds;
  private readonly lotto: Lotto;
  private readonly neighbors: Neighbors;

  private readonly hud: Hud;
  private readonly modal: SceneModal;
  private readonly shop: ShopUI;
  private readonly phone: Phone;
  private readonly scratch: ScratchOff;
  private readonly tv: TvScreen;
  private readonly title: TitleScreen;
  private readonly creator: CharacterCreator;
  /** How far you've spun your character around in the creator. */
  private creatorTurn = 0;
  /** Under your feet, where notifications pop up. */
  private readonly toastAt = new THREE.Vector3();
  private readonly end: EndScreen;
  private readonly touch: TouchControls;
  private readonly online: OnlinePanel;
  private readonly stages: StageHost;

  private readonly portraits = new Map<string, string>();
  private settings = loadSettings();
  private mode: 'title' | 'play' = 'title';
  private time = 0;
  private last = performance.now();
  private hudTimer = 0;
  private barkTimer = 6;
  private autosaveTimer = 30;
  private fundTimer = 1;
  private trafficCooldown = 0;
  private suppressUntil = 0;
  private parkSign: 'classic' | 'apex' | 'owned' | null = null;
  /** 0..1 gloom while you're in the Back Forty. */
  private murk = 0;
  private backFortySeen = -Infinity;
  private readonly tmp = new THREE.Vector3();
  private readonly eye = new THREE.Vector3();
  private staged = false;
  private readonly highlighter: Highlighter;
  /** Your flashlight or headlamp. */
  private readonly lamp: Lamp;
  private readonly lampDir = new THREE.Vector3();
  private readonly lampFrom = new THREE.Vector3();
  private readonly foilHat = foilHatProp();
  private readonly markerTops = new WeakMap<THREE.Object3D, THREE.Vector3>();

  constructor(canvas: HTMLCanvasElement, uiRoot: HTMLElement) {
    const touch = isTouch();
    // High-DPI phones don't need MSAA on top of their pixel density; it mostly costs battery.
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: !(touch && window.devicePixelRatio >= 2),
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, touch ? 1.5 : 1.75));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.shadowMap.enabled = this.settings.shadows;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.3, 1000);
    this.cameraRig = new CameraRig(this.camera);
    // Desktop GPUs can afford a sharper shadow map; phones keep the lighter one.
    this.sky = new Sky(this.scene, touch ? 2048 : 4096);
    this.sky.sun.castShadow = this.settings.shadows;

    this.world = new World(this.scene);
    this.world.build();
    this.highlighter = new Highlighter(this.scene);
    this.lamp = new Lamp(this.scene);
    this.cameraRig.occluders = this.world.occluders;
    this.world.setParticleScale(window.innerHeight);

    this.store = new Store(createInitialState({ name: 'Bubba', look: DEFAULT_LOOK, seed: 1 }));
    this.input = new Input(canvas);

    this.localHome = this.world.placeHome(LOT_7, trailerStyle(DEFAULT_LOOK), String(LOT_7.number));
    this.world.setPorchLight(this.localHome);
    this.player = new Player(this.world.dynamic, DEFAULT_LOOK);
    this.player.teleport(TITLE_SPOT.x, TITLE_SPOT.z, 0);
    this.npcs = new NpcManager(NPCS, this.world.dynamic, this.rng);
    this.decor = new DecorManager(this.world, this.world.dynamic, this.world.collision, LOT_7);
    this.pickups = new Pickups(this.world.dynamic, this.world.collision, this.rng);
    this.effects = new Effects(this.scene, this.world, this.sky, this.rng, (n) => this.sfx(n));

    const worldFx: WorldEffects = {
      tornado: () => {
        this.effects.startTornado();
        this.neighbors.share({ kind: 'tornado' });
      },
      cops: () => {
        this.effects.startCops();
        this.neighbors.share({ kind: 'cops' });
        const dale = this.npcs.get('dale');
        dale?.flee(true);
        window.setTimeout(() => dale?.flee(false), 40_000);
      },
      lostDog: () => {
        this.effects.startChase();
        this.store.setFlag('chase_until', this.store.now + 180);
      },
      fireworks: () => {
        const { x, z } = this.player.position;
        this.effects.launchFireworks(x, z + 3, 5);
        this.neighbors.share({ kind: 'fireworks', x, z: z + 3 });
      },
      scatterScrap: (n) => this.pickups.scatter('scrap', n, 0, -4, 32),
      spawnCans: (n) => this.pickups.spawn('can', n),
      shake: (i, s) => this.effects.shake(i, s),
      teleportPlayer: (spot) => this.teleportTo(spot),
      zone: () =>
        backFortyDepth(this.player.position.x, this.player.position.z) > 0.5 ? 'backForty' : 'park',
    };
    const contentUi: ContentUi = {
      openScene: (scene) => this.modal.show(scene),
      openShop: (kind) => this.shop.open(kind),
      watchTv: () => this.activities.watchTv(),
      playCornhole: (bet) => this.backForty.playCornhole(bet),
      play: (game, bet) => this.games.play(game, bet),
    };
    this.ctx = { store: this.store, rng: this.rng, world: worldFx, ui: contentUi, sfx: (n) => this.sfx(n) };

    // --- UI ---
    this.touch = new TouchControls(uiRoot, this.input);
    this.hud = new Hud(uiRoot, this.store, {
      onPhone: () => this.phone.toggle(),
      onMusic: () => this.toggleMusic(),
      onSlot: (id) => this.useItem(id),
      onPrompt: () => this.input.press('KeyE'),
      onOnline: () => this.phone.openChat(),
    });
    const meter = new StuntMeter(uiRoot, () => this.input.press('Space'));
    const onOpen = (): void => undefined;
    const onClose = (): void => this.onUiClosed();
    this.modal = new SceneModal(uiRoot, {
      ctx: this.ctx,
      portrait: (id) => this.portraits.get(id),
      blip: (p) => this.audio.blip(p),
      onOpen,
      onClose,
    });
    this.shop = new ShopUI(uiRoot, {
      store: this.store,
      rng: this.rng,
      sfx: (n) => this.sfx(n),
      onOpen,
      onClose,
      acceptJob: (offer) => this.jobs.accept(offer),
      cancelJob: () => this.jobs.cancel(),
      onCamaroSold: () => {
        // If you've got something else, it moves into the driveway.
        const st = this.store.state;
        if (st.ride === 'camaro' && st.rides.length) st.ride = st.rides[0] as string;
        this.driving.reset();
      },
      onRideChanged: () => this.driving.reset(),
      onRascalBought: () => this.rascal.delivered(),
      onWin: () => this.win(),
      homeKind: () => this.homeKind(),
      fund: () => this.fundView(),
    });
    this.phone = new Phone(uiRoot, {
      store: this.store,
      rng: this.rng,
      portrait: (id) => this.portraits.get(id),
      sfx: (n) => this.sfx(n),
      onOpen,
      onClose,
      onSave: () => this.save(),
      onQuit: () => this.toTitle(),
      settings: () => this.settings,
      onSettings: (s) => this.applySettings(s),
      online: { mount: (el) => this.online.mount(el), focusChat: () => this.online.focusChat() },
      hustles: { show: (id) => this.hustles.show(id) },
    });
    this.scratch = new ScratchOff(uiRoot, { sfx: (n) => this.sfx(n), onOpen, onClose });
    this.stages = new StageHost(uiRoot, canvas, { onOpen, onClose });
    this.pads = new Gamepads({
      input: this.input,
      stageLook: (dx, dy) => this.stages.look(dx, dy),
      nav: new PadNav(),
      onDeviceChange: (pad) => this.hud.setPad(pad),
      onFirstPress: () => this.audio.init(),
    });
    this.viewScene.add(new THREE.HemisphereLight('#fff6e8', '#4a3a2a', 1.7), this.viewRoot);
    const key = new THREE.DirectionalLight('#fff1dc', 1.6);
    key.position.set(0.6, 1, 0.4);
    this.viewScene.add(key);
    this.tv = new TvScreen(uiRoot, {
      rng: this.rng,
      pick: () => pickShow(this.rng, this.store.hasDecor('dish')),
      sfx: (n) => this.sfx(n),
      onOpen,
      onClose,
    });
    this.title = new TitleScreen(uiRoot, {
      onNewGame: () => this.openCreator(),
      onContinue: () => this.continueGame(),
      onSkipPark: () => {
        this.neighbors.pending = null;
        this.title.setPark(null);
      },
      settings: () => this.settings,
      onSettings: (s) => this.applySettings(s),
    });
    this.creator = new CharacterCreator(
      uiRoot,
      {
        rng: this.rng,
        onLook: (look) => this.player.setLook(look),
        onTurn: (radians) => (this.creatorTurn += radians),
        onBack: () => this.showTitle(),
        onDone: (name, look) => this.newGame(name, look),
        thumb: (look, frame) => renderPortrait(this.renderer, look, 128, frame),
      },
      DEFAULT_LOOK,
    );
    this.end = new EndScreen(uiRoot);

    this.jobs = new Jobs(this.ctx, this.world.dynamic, this.player, this.world.sparks, () =>
      this.audio.loop('mower'),
    );
    this.stunts = new StuntController(this.player, meter, this.rng, (n) => this.sfx(n));

    // --- gameplay modules ---
    this.services = {
      store: this.store,
      rng: this.rng,
      ctx: this.ctx,
      input: this.input,
      world: this.world,
      decor: this.decor,
      effects: this.effects,
      player: this.player,
      npcs: this.npcs,
      audio: this.audio,
      cameraRig: this.cameraRig,
      jobs: this.jobs,
      stunts: this.stunts,
      ui: {
        hud: this.hud,
        modal: this.modal,
        shop: this.shop,
        scratch: this.scratch,
        tv: this.tv,
        stages: this.stages,
      },
      now: () => this.time,
      sfx: (n) => this.sfx(n),
      save: () => this.save(),
      onUiClosed: () => this.onUiClosed(),
      teleportTo: (spot) => this.teleportTo(spot),
      onNewDay: () => this.onNewDay(),
      share: (fx) => this.neighbors.share(fx),
      home: () => this.localHome.spots,
      view: this.viewRoot,
      setLook: (look) => this.changeLook(look),
    };
    this.driving = new Driving(this.services);
    this.driving.reset();
    this.rest = new RestCycle(this.services, this.driving);
    this.stuntFlow = new StuntFlow(this.services);
    this.activities = new Activities(this.services, this.rest, this.driving);
    this.activities.toggleLamp = () => this.toggleLamp();
    this.activities.toggleFoilHat = () => {
      const on = !this.store.flag('foil_on');
      this.store.setFlag('foil_on', on);
      this.wearFoilHat();
      this.sfx('scratch');
      this.store.toast(
        on
          ? 'Foil hat ON. You can feel the signals. Or a breeze. Hard to say.'
          : 'Foil hat off. Now they can hear you think. Probably.',
        'meme',
        ':foil-hat:',
      );
    };
    registerInteractions(this.services, this.interactions, {
      activities: this.activities,
      driving: this.driving,
      stunts: this.stuntFlow,
    });
    this.backForty = new BackForty(this.services, this.activities, this.rest, this.stages);
    this.backForty.register(this.interactions);
    this.interiors = new Interiors(this.services, this.activities, this.rest, {
      myIndex: () => this.localHome.frame.index,
      myKind: () => this.homeKind(),
      neighborHomes: () => this.neighbors.visitableHomes(),
      openDecorate: () => this.shop.open('decorate'),
      darkness: () => this.sky.darkness,
    });
    this.interiors.register(this.interactions);
    this.games = new Games(this.services, this.stages, {
      mount: (kind) => (kind === 'rodeo' ? this.rodeoMount() : this.strip.bullMount()),
      bar: () => this.strip.bar,
    });
    this.strip = new Strip(this.services, this.interiors, this.games);
    this.strip.register(this.interactions);
    this.shops = new Shops(this.services, this.interiors, this.activities);
    this.shops.register(this.interactions);
    this.townsfolk = new Townsfolk(this.services, this.interiors, this.activities);
    this.townsfolk.register(this.interactions);
    this.parkHomes = new ParkHomes(this.services, this.interiors, this.activities);
    this.parkHomes.register(this.interactions);
    this.campfires = new Campfires(this.services, this.activities);
    this.campfires.register(this.interactions);
    this.abduction = new Abduction(this.services, this.interiors, this.activities, this.rest);
    this.abduction.register(this.interactions);
    this.rascal = new Rascal(this.services, { driving: this.driving, rest: this.rest, stages: this.stages });
    this.rascal.register(this.interactions);
    this.hustles = new Hustles(this.services);
    this.favors = new Favors(this.services, this.activities);
    this.favors.register(this.interactions);
    this.carWash = new CarWash(this.services);
    this.carWash.register(this.interactions);
    this.deliveries = new Deliveries(this.services, this.activities);
    this.deliveries.register(this.interactions);
    this.shineRuns = new ShineRuns(this.services, this.activities, this.driving);
    this.shineRuns.register(this.interactions);
    this.landlord = new Landlord(this.services, this.activities);
    this.landlord.register(this.interactions);
    this.fairgrounds = new Fairgrounds(this.services, this.driving, this.games);
    this.fairgrounds.register(this.interactions);
    this.lotto = new Lotto(this.services);
    this.neighbors = new Neighbors(this.services, {
      interactions: this.interactions,
      activities: this.activities,
      driving: this.driving,
      rest: this.rest,
      portraits: this.portraits,
      renderPortrait: (look) => renderPortrait(this.renderer, look),
      inMenu: () =>
        this.modal.isOpen ||
        this.shop.isOpen ||
        this.phone.isOpen ||
        this.scratch.isOpen ||
        this.tv.isOpen ||
        this.stages.isOpen,
      homeIndex: () => this.localHome.frame.index,
      moveHome: (index) => this.placeLocalHome(homeLot(index)),
      night: () => this.sky.darkness,
      stages: this.stages,
      fund: () => this.fundView(),
      lampOn: () => this.lamp.on !== null,
      ufo: (x, z) => this.abduction.watch(x, z),
    });
    this.online = new OnlinePanel(this.neighbors);

    this.director = new Director(this.ctx, {
      canInterrupt: () => !this.blocked && !this.stunts.active && !this.player.busy,
      allowRandom: () =>
        !this.effects.chaseTarget &&
        !this.driving.active &&
        !this.shineRuns.active &&
        !this.deliveries.active &&
        (this.store.state.job?.stage ?? 'start') === 'start',
      onEvicted: () => undefined,
    });

    trackAchievements(this.store);
    this.wireStore();
    this.renderPortraits();
    for (let i = 0; i < 5; i++) this.hud.pushTicker(tickerItem(this.rng));
    this.hud.setMusic(this.audio.musicEnabled);
    this.applySettings(this.settings);

    window.addEventListener('resize', this.onResize);
    onTouchChange(() => this.applyViewport());
    this.applyViewport();
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.mode === 'play') this.save();
    });
    // Browsers (iOS Safari especially) only allow audio to start inside a real user gesture
    // (not the key events a game controller is turned into), so keep asking until it's on.
    const gestures = ['pointerdown', 'touchend', 'keydown'] as const;
    const unlockAudio = (e: Event): void => {
      if (!e.isTrusted) return;
      this.audio.init();
      if (this.audio.running) for (const type of gestures) window.removeEventListener(type, unlockAudio);
    };
    for (const type of gestures) window.addEventListener(type, unlockAudio);

    this.showTitle();
    if (import.meta.env.DEV) (window as unknown as { __game: Game }).__game = this;
  }

  start(): void {
    this.last = performance.now();
    requestAnimationFrame(this.frame);
  }

  // ---------------------------------------------------------------------------
  // Plumbing
  // ---------------------------------------------------------------------------

  private wireStore(): void {
    const s = this.store;
    s.bus.on('decor', (owned) => this.decor.sync(owned));
    s.bus.on('float', ({ text, color }) => this.hud.float(text, this.player.headPosition(), color));
    s.bus.on('money', (delta) => {
      if (this.mode !== 'play' || Math.abs(delta) < 0.5) return;
      const at = this.player.headPosition(this.tmp).setY(this.player.y + 2.4);
      this.hud.float(
        `${delta > 0 ? '+' : '-'}${formatMoney(Math.abs(delta))}`,
        at,
        delta > 0 ? '#9dff6a' : '#ff8a8a',
      );
    });
    s.bus.on('achievement', () => this.sfx('airhorn'));
    s.bus.on('change', () => {
      const sign = s.flag('park_owned') ? 'owned' : s.flag('apex') ? 'apex' : 'classic';
      if (sign !== this.parkSign) {
        this.parkSign = sign;
        this.world.setParkSign(sign);
      }
    });
  }

  private renderPortraits(): void {
    for (const def of NPCS) this.portraits.set(def.id, renderPortrait(this.renderer, def.look));
    this.refreshPlayerPortrait();
  }

  /** Hank's hat: a crinkly foil cone on your head. */
  private wearFoilHat(): void {
    this.foilHat.removeFromParent();
    if (this.store.flag('foil_on') && this.store.has('foilHat')) this.player.rig.head.add(this.foilHat);
  }

  /** New clothes or a new haircut: same you, different look. */
  private changeLook(look: CharacterLook): void {
    this.store.state.player.look = look;
    this.player.setLook(look);
    this.lamp.reattach(this.player.rig);
    this.wearFoilHat();
    this.refreshPlayerPortrait();
    this.neighbors.lookChanged();
    this.store.changed();
  }

  private refreshPlayerPortrait(): void {
    this.portraits.set('player', renderPortrait(this.renderer, this.store.state.player.look));
    this.hud.setAvatar(this.portraits.get('player') ?? '');
  }

  private applySettings(s: Settings): void {
    const shadowsChanged = s.shadows !== this.renderer.shadowMap.enabled;
    this.settings = s;
    this.setFirstPerson(s.firstPerson);
    this.stages.invertY = s.invertY;
    this.audio.setVolumes(s.music, s.sfx);
    if (shadowsChanged) {
      this.renderer.shadowMap.enabled = s.shadows;
      this.sky.sun.castShadow = s.shadows;
      // Materials compile differently with shadows on/off.
      this.scene.traverse((o) => {
        const m = (o as THREE.Mesh).material;
        if (Array.isArray(m)) m.forEach((x) => (x.needsUpdate = true));
        else if (m) m.needsUpdate = true;
      });
    }
    storeSettings(s);
  }

  /** Third person, or your own two eyes. The near plane comes in so walls don't clip the view. */
  private setFirstPerson(on: boolean): void {
    const rig = this.cameraRig;
    if (rig.firstPerson === on) return;
    rig.firstPerson = on;
    if (on) rig.lookPitch = clamp(0.3 - rig.pitch, -0.5, 0.2);
    else {
      rig.distance = Math.max(rig.distance, rig.minDistance + 1.5);
      rig.snap();
    }
    this.camera.near = on ? 0.12 : 0.3;
    this.camera.updateProjectionMatrix();
  }

  private toggleFirstPerson(): void {
    const on = !this.cameraRig.firstPerson;
    this.applySettings({ ...this.settings, firstPerson: on });
    if (on && !this.store.flag('fp_tip')) {
      this.store.setFlag('fp_tip', true);
      this.store.toast(
        isTouch()
          ? 'First person! Drag to look around. :eye: switches back.'
          : 'First person! Drag the mouse to look. V (or scroll out) switches back.',
        'info',
        ':eye:',
      );
    }
  }

  private readonly onResize = (): void => this.applyViewport();

  private applyViewport(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    // Portrait phones get a wider lens, or you'd see the park through a mail slot.
    this.camera.fov = isTouch() && h > w ? 72 : 60;
    this.camera.updateProjectionMatrix();
    this.world.setParticleScale(h);
  }

  private sfx(name: SfxName): void {
    this.audio.play(name);
  }

  private toggleMusic(): void {
    this.audio.init();
    this.hud.setMusic(this.audio.toggleMusic());
  }

  private get blocked(): boolean {
    return (
      this.modal.isOpen ||
      this.shop.isOpen ||
      this.phone.isOpen ||
      this.scratch.isOpen ||
      this.tv.isOpen ||
      this.stages.isOpen ||
      this.title.isOpen ||
      this.creator.isOpen ||
      this.end.isOpen ||
      this.rest.sleeping
    );
  }

  /** What a game controller is driving this frame. */
  private padContext(): PadContext {
    if (this.mode === 'title') return 'menu';
    if (this.stages.playing) return 'stage';
    if (this.blocked) return 'menu';
    return this.driving.active ? 'drive' : 'play';
  }

  private onUiClosed(): void {
    this.input.endFrame();
    this.suppressUntil = this.time + 0.2;
  }

  private save(): void {
    if (this.mode === 'play') saveGame(this.store.state);
  }

  private teleportTo(spot: SpotId): void {
    const p = SPOTS[spot];
    this.player.teleport(p.x, p.z);
    this.cameraRig.snap();
  }

  private useItem(id: ItemId): void {
    if (this.mode !== 'play' || this.blocked) return;
    if (id === 'shine') this.backForty.drinkShine();
    else if (id === 'rascal') this.rascal.toggle();
    else this.activities.useItem(id);
  }

  // ---------------------------------------------------------------------------
  // Title / new game / end
  // ---------------------------------------------------------------------------

  /** The start screen. Your saved character (if you have one) waves from the yard. */
  private showTitle(): void {
    this.mode = 'title';
    const saved = loadGame();
    const playing = saved && saved.status === 'playing' ? saved : null;
    const info = playing ? `${playing.player.name} · Day ${playing.time.day}` : null;
    this.hud.visible = false;
    this.touch.visible = false;
    this.creator.hide();
    const park = this.neighbors.pending;
    this.title.setPark(park?.code ?? null, park?.invited);
    this.title.show(info);
    this.player.setLook(playing?.player.look ?? DEFAULT_LOOK);
    this.player.teleport(TITLE_SPOT.x, TITLE_SPOT.z + 1.5, TITLE_FACING);
  }

  /** New Game: the character creator, with the camera up close. */
  private openCreator(): void {
    this.title.hide();
    this.creatorTurn = 0;
    this.player.teleport(TITLE_SPOT.x, TITLE_SPOT.z + 1.5, TITLE_FACING);
    this.creator.show(DEFAULT_LOOK);
  }

  private toTitle(): void {
    this.save();
    this.neighbors.exitPlay();
    this.driving.stop(true);
    this.rascal.reset();
    this.favors.reset();
    this.carWash.reset();
    this.deliveries.reset();
    this.shineRuns.reset();
    this.landlord.reset();
    this.stunts.cancel();
    this.jobs.cancel(false);
    this.effects.endChase();
    this.end.hide();
    this.showTitle();
  }

  /**
   * Builds your home on a lot, in your trailer colors, and moves your yard and Camaro
   * there. If you were standing at your old place, you come along too.
   */
  private placeLocalHome(frame: HomeFrame): void {
    const old = this.localHome;
    const kind = this.homeKind();
    const p = this.player.position;
    const wasHome = inRect(old.spots.area, p.x, p.z, 2);
    const moved = old.frame.index !== frame.index;
    const rebuilt = moved || old.kind !== kind;
    if (moved) {
      this.driving.stop(true);
      this.stunts.cancel();
    }
    this.world.removeHome(old.frame.index);
    this.localHome = this.world.placeHome(
      frame,
      trailerStyle(this.store.state.player.look),
      String(frame.number),
      kind,
    );
    this.world.setPorchLight(this.localHome);
    if (rebuilt) {
      this.decor.setFrame(frame, kind);
      this.driving.reset();
      this.rascal.rehome(old.spots.area);
      if (wasHome && this.mode === 'play') {
        const spawn = this.localHome.spots.spawn;
        this.player.teleport(spawn.x, spawn.z, 0);
        this.cameraRig.snap();
      }
    }
    this.interiors.syncMine();
  }

  /** Everyone's contributions to buying the park back (yours plus the neighbors here now). */
  private fundView(): { total: number; goal: number; players: number } {
    const others = this.neighbors.fundContributions();
    const players = 1 + others.length;
    const total = this.store.state.fund + others.reduce((a, b) => a + b, 0);
    return { total, goal: CONFIG.buybackGoal + CONFIG.buybackPerNeighbor * (players - 1), players };
  }

  /** The jar's full: Lucky Acres belongs to the people who live here. */
  private checkBuyback(): void {
    const s = this.store;
    if (s.flag('park_owned')) return;
    const f = this.fundView();
    if (f.total < f.goal) return;
    s.setFlag('park_owned', s.day);
    s.unlock('park_owned');
    this.sfx('airhorn');
    const { x, z } = SPOTS.parkSign;
    this.effects.launchFireworks(x, z + 4, 12);
    this.neighbors.share({ kind: 'fireworks', x, z: z + 4 });
    s.headline(localHeadline('buyback', s.state.player.name));
    this.modal.show({
      title: 'Lucky Acres Is Ours',
      art: ':trophy:',
      speaker: { id: 'darlene', name: 'Darlene · Park Manager', voice: 330 },
      text: `'${f.players > 1 ? "Y'all" : 'You'} did it. $${Math.round(f.total).toLocaleString()} in a pickle jar, and Apex took it. Brent signed the deed in his car with the engine running.\n\nLucky Acres is RESIDENT OWNED. No more rent hikes. Just $${CONFIG.coopDues} a week in co-op dues, and I'm still in charge of the dumpster.'`,
      choices: [],
      leave: 'Power to the people',
    });
  }

  /** Widowmaker, in the chute at the rodeo arena. */
  private rodeoMount(): BullMount {
    const bull = this.world.fair.bull;
    return {
      kind: 'rodeo',
      bull: bull.group,
      head: bull.head,
      backY: 1.62,
      arena: { x: RODEO.x - 2.5, z: RODEO.z, r: 4.5 },
      exit: SPOTS.rodeoChute,
      orbit: { x: RODEO.x - 2, z: RODEO.z + 1, r: 8, y: 2.8 },
      setBusy: (busy) => (bull.busy = busy),
      parent: this.world.dynamic,
    };
  }

  /** A single-wide, until you sign the papers at Big Dave's. */
  private homeKind(): HomeKind {
    const f = this.store.state.flags;
    return f.double_wide || f.won ? 'double' : 'single';
  }

  private resetWorldForState(): void {
    const st = this.store.state;
    this.placeLocalHome(this.localHome.frame);
    this.decor.sync(st.decor);
    this.driving.reset();
    this.rascal.reset();
    this.favors.reset();
    this.carWash.reset();
    this.deliveries.reset();
    this.shineRuns.reset();
    this.landlord.reset();
    this.pickups.clear();
    this.pickups.spawn('can', 10);
    this.pickups.spawn('scrap', 6);
    this.pickups.spawn('scrap', 5, BACK_FORTY_LITTER);
    this.decor.setGooseOutfit(st.time.day);
    this.parkSign = null;
    this.store.changed();
    this.refreshPlayerPortrait();
  }

  private enterPlay(): void {
    this.audio.init();
    this.title.hide();
    this.creator.hide();
    // Don't leave a menu button focused: Space or Enter would press it again.
    (document.activeElement as HTMLElement | null)?.blur?.();
    this.mode = 'play';
    this.hud.visible = true;
    this.touch.visible = true;
    this.camera.clearViewOffset();
    const spawn = this.localHome.spots.spawn;
    this.player.teleport(spawn.x, spawn.z, 0);
    this.cameraRig.yaw = 0;
    this.cameraRig.pitch = 0.38;
    this.cameraRig.distance = isTouch() && isPortrait() ? 9.5 : 8;
    this.cameraRig.snap();
    this.onUiClosed();
    this.neighbors.enterPlay();
  }

  private newGame(name: string, look: CharacterLook): void {
    this.store.replace(
      createInitialState({ name: name.slice(0, 18), look, seed: (Date.now() ^ this.rng.int(0, 1e9)) >>> 0 }),
    );
    this.player.setLook(look);
    this.lamp.set(null, this.player.rig);
    this.resetWorldForState();
    for (let i = 0; i < 3; i++) this.store.post(randomPost(this.rng, false));
    this.enterPlay();
    this.save();
    this.modal.show(
      {
        title: 'Welcome to Lucky Acres',
        speaker: { id: 'darlene', name: 'Darlene · Park Manager', voice: 330 },
        text: `'Well, look what the cat drug in. You must be ${name}. Your uncle left you Lot 7: one single-wide, one cooler and one 1987 Camaro that ain't moved since the Bush administration. Which one? Yes.\n\nLot rent's $${CONFIG.rentBase}, due Fridays at 5. Cash or money order. Three strikes and you're out, sugar. I don't make the rules. Well, I do. But still.'`,
        choices: [],
        leave: "Yes ma'am",
      },
      () =>
        this.modal.show({
          title: 'The Dream',
          art: ':double-wide:',
          text: `GOAL: Save up $${CONFIG.doubleWidePrice.toLocaleString()} for a DOUBLE WIDE at Big Dave's (right by the park entrance).\n\n• Pay lot rent every Friday by 5 PM.\n• Collect cans & scrap (they sparkle). Sell to Junkyard Jerry.\n• Odd jobs on the board at Suds-N-Duds, across the highway.\n• Quick cash: a :dollar: over a neighbor's head means they need a hand. Your phone's :briefcase: Hustles has every way to make a buck (do the easy ones and bigger ones open up).\n• Fix the Camaro. Buy yard decor. Become ${byBody(look, 'Lord', 'Lady')} of Lucky Acres.\n\n${
            isTouch()
              ? 'Left thumb: joystick to walk · :croc: for Crocs SPORT MODE · E to interact · :beer: to crack a cold one · drag the right side to look, pinch to zoom.'
              : 'WASD to move · Shift for Crocs SPORT MODE · E to interact · Q to crack a cold one · P for your phone.'
          }`,
          choices: [],
          leave: "Let's git-r-done",
        }),
    );
  }

  private continueGame(): void {
    const loaded = loadGame();
    if (!loaded) return;
    Hustles.quiet(loaded);
    this.store.replace(loaded);
    this.player.setLook(loaded.player.look);
    this.lamp.set(null, this.player.rig);
    this.wearFoilHat();
    this.resetWorldForState();
    this.enterPlay();
    this.store.toast(
      `Welcome back, ${loaded.player.name}. Day ${loaded.time.day}, ${dayName(loaded.time.day)}.`,
      'good',
      ':trailer:',
    );
  }

  private win(): void {
    this.save();
    this.audio.stinger('win');
    this.effects.launchFireworks(SPOTS.bigDaveHome.x, SPOTS.bigDaveHome.z + 6, 10);
    this.store.headline(localHeadline('doublewide', this.store.state.player.name));
    this.end.show('won', this.store, {
      primary: [
        ":double-wide: Keep livin' (sandbox)",
        () => {
          this.store.state.status = 'playing';
          this.store.setFlag('won', true);
          this.end.hide();
          this.onUiClosed();
          // Big Dave's crew hauls off the single-wide and sets the double wide on your lot.
          this.placeLocalHome(this.localHome.frame);
          this.store.toast(
            "Big Dave's crew dropped your DOUBLE WIDE on your lot. Two bathrooms. You'll never fight for the toilet again.",
            'good',
            ':double-wide:',
          );
          this.save();
        },
      ],
      secondary: [':repeat: New game', () => this.toTitle()],
    });
  }

  private evicted(): void {
    this.save();
    this.audio.stinger('evicted');
    this.store.headline(localHeadline('evicted', this.store.state.player.name));
    this.end.show('evicted', this.store, {
      primary: [':repeat: Try again', () => this.toTitle()],
      secondary: [':scroll: Back to title', () => this.toTitle()],
    });
  }

  private onNewDay(): void {
    const s = this.store;
    this.pickups.spawn('can', 8);
    this.pickups.spawn('scrap', 5);
    this.pickups.spawn('scrap', 4, BACK_FORTY_LITTER);
    for (let i = 0; i < 2; i++) s.post(randomPost(this.rng, !!s.flag('apex')));
    this.decor.setGooseOutfit(s.day + Number(s.flag('goose_outfit') ?? 0));
    this.hud.pushTicker(tickerItem(this.rng));
    this.rascal.newDay();
  }

  // ---------------------------------------------------------------------------
  // Main loop
  // ---------------------------------------------------------------------------

  private readonly frame = (now: number): void => {
    requestAnimationFrame(this.frame);
    const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    this.tick(dt);
  };

  /** One simulation + render step. Separate from frame scheduling so it can be stepped in tests. */
  private tick(dt: number): void {
    this.pads.update(dt, this.padContext());
    this.time += dt;
    if (this.mode === 'title') this.updateTitle(dt);
    else this.updatePlay(dt);
    this.render(dt);
    this.input.endFrame();
  }

  private updateTitle(dt: number): void {
    if (this.creator.isOpen) {
      this.updateCreator(dt);
      return;
    }
    // Sweep back and forth across the front yard (never behind the trailer).
    const a = Math.sin(this.time * 0.15) * 0.9 - 0.35;
    const target = v3(TITLE_SPOT.x, TITLE_SPOT.z + 1.5, 0);
    this.camera.position.set(
      target.x + Math.sin(a) * 7,
      2.2 + Math.sin(this.time * 0.11) * 0.3,
      target.z + Math.cos(a) * 7,
    );
    this.camera.lookAt(target.x - 0.2, 1.3, target.z);
    this.frameTitleForTouch();
    this.player.anim.update({ dt, time: this.time, speed: 0, pose: 'wave' });
    this.player.syncTransform();
    this.npcs.update(dt, this.time, 18.5 * 60, this.world.collision);
    this.murk = 0;
    this.sky.murk = 0;
    this.renderer.toneMappingExposure = 1.05;
    this.sky.update(18.6 * 60, dt, target, this.camera);
    this.effects.update(dt, this.time, this.player.position, this.camera, this.sky.darkness);
  }

  /**
   * The creator: the camera stands in front of your character, framed in whatever part of
   * the screen the panel leaves open, and dragging spins the character around.
   */
  private updateCreator(dt: number): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const panel = this.creator.panelRect();
    // The open part of the screen: left of the panel, or above it on a phone held upright.
    const below = panel && panel.top > h * 0.3;
    const right = panel && !below && panel.left > w * 0.3;
    const freeW = right && panel ? panel.left : w;
    const freeH = below && panel ? panel.top : h;
    const shiftX = (w - freeW) / 2;
    const shiftY = (h - freeH) / 2;
    if (shiftX || shiftY) this.camera.setViewOffset(w, h, shiftX, shiftY, w, h);
    else if (this.camera.view?.enabled) this.camera.clearViewOffset();
    // Back off until a whole person fits in the open part (with a little headroom).
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
    const fitH = 3.3 / (2 * tanHalf * (freeH / h));
    const fitW = 1.5 / (2 * tanHalf * this.camera.aspect * (freeW / w));
    const dist = THREE.MathUtils.clamp(Math.max(fitH, fitW), 2.6, 9);
    const target = v3(TITLE_SPOT.x, TITLE_SPOT.z + 1.5, 0);
    this.camera.position.set(
      target.x + Math.sin(TITLE_FACING) * dist,
      1.25,
      target.z + Math.cos(TITLE_FACING) * dist,
    );
    this.camera.lookAt(target.x, 0.98, target.z);
    this.player.facing = TITLE_FACING + this.creatorTurn;
    this.player.anim.update({ dt, time: this.time, speed: 0, pose: 'stand' });
    this.player.syncTransform();
    this.npcs.update(dt, this.time, 18.5 * 60, this.world.collision);
    this.murk = 0;
    this.sky.murk = 0;
    // A little earlier in the evening than the menu, so you can see what you're wearing.
    this.renderer.toneMappingExposure = 1.1;
    this.sky.update(17.2 * 60, dt, target, this.camera);
    this.effects.update(dt, this.time, this.player.position, this.camera, this.sky.darkness);
  }

  /**
   * On phones the title UI covers part of the screen, so slide the rendered image
   * to keep your character in the open space. Desktop framing is left alone.
   */
  private frameTitleForTouch(): void {
    if (!isTouch()) {
      if (this.camera.view?.enabled) this.camera.clearViewOffset();
      return;
    }
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (h > w) this.camera.setViewOffset(w, h, 0, h * 0.16, w, h);
    else if (h < 560) this.camera.setViewOffset(w, h, w * 0.24, -h * 0.04, w, h);
    else this.camera.clearViewOffset();
  }

  private updatePlay(dt: number): void {
    const s = this.store;
    this.stages.update(dt);
    // Before anything else this frame: if a mini-game, the couch or bed took you, you're off the Rascal.
    this.rascal.update(dt, this.time, this.sky.darkness);
    // A mini-game out in the world gets the screen to itself: no status bars or joystick.
    const staged = this.stages.playing;
    if (staged !== this.staged) {
      this.staged = staged;
      this.hud.stageMode = staged;
      this.touch.visible = !staged;
      if (!staged) this.cameraRig.snap();
    }
    const blocked = this.blocked;
    this.updateMurk(dt);
    this.touch.setDriving(this.driving.active);
    this.touch.setLamp(s.has('flashlight') || s.has('headlamp'), this.lamp.on !== null);
    this.trafficCooldown -= dt;

    if (s.state.status === 'evicted' && !this.modal.isOpen && !this.end.isOpen) this.evicted();

    if (!blocked && s.state.status === 'playing') {
      const newDays = s.advanceTime(dt * CONFIG.minutesPerSecond);
      if (newDays > 0) this.onNewDay();
      this.zoomIntoFirstPerson();
      this.cameraRig.orbit(
        this.input.lookDX,
        this.input.lookDY * (this.settings.invertY ? -1 : 1),
        this.input.wheel,
      );
      this.handleHotkeys();
      this.updateMovement(dt);
      this.collectPickups();
      this.updateChase();
      this.jobs.update();
      this.favors.update(
        dt,
        this.time,
        !this.driving.active &&
          !this.carWash.open &&
          !this.deliveries.active &&
          !this.shineRuns.active &&
          (s.state.job?.stage ?? 'start') === 'start',
      );
      this.carWash.update(dt, this.time);
      this.deliveries.update();
      this.shineRuns.update(dt, this.time);
      this.landlord.update(dt, this.time);
      this.parkHomes.update();
      this.fairgrounds.update(dt);
      this.shops.update();
      this.campfires.update(this.time, dt);
      this.abduction.update(
        dt,
        !this.driving.active && !this.stunts.active && (s.state.job?.stage ?? 'start') === 'start',
      );
      if (!this.modal.isOpen) this.lotto.update(dt);
      this.director.update();
      this.checkVitals();
      this.fundTimer -= dt;
      if (this.fundTimer <= 0) {
        this.fundTimer = 1;
        this.checkBuyback();
      }
      this.autosaveTimer -= dt;
      if (this.autosaveTimer <= 0) {
        this.autosaveTimer = 30;
        this.save();
      }
    } else {
      this.player.update(dt, this.time, { dx: 0, dz: 0, sprint: false, jump: false }, this.world.collision, {
        buzz: s.stat('buzz'),
        energy: s.stat('energy'),
        // On the Rascal with your phone out, you're still sitting on the Rascal.
        poseOverride: this.rascal.riding ? 'scoot' : undefined,
      });
    }

    this.npcs.update(dt, this.time, s.minutes, this.world.collision);
    this.neighbors.update(dt);
    this.updateBarks(dt);
    if (this.effects.update(dt, this.time, this.player.position, this.camera, this.sky.darkness).caught)
      this.caughtPeanut();
    this.updatePrompt(blocked);
    // Inside, the objective arrow would just point at a wall.
    const obj = this.interiors.inside ? null : this.objective();
    this.hud.setObjective(obj?.text ?? null, obj?.target ?? null, this.camera, this.player.position);

    this.hudTimer -= dt;
    if (this.hudTimer <= 0) {
      this.hudTimer = 0.1;
      this.hud.update();
      this.hud.setUnread(this.phone.isOpen ? 0 : this.phone.unread);
      this.hud.setRide(this.rascal.riding ? this.rascal.battery : null);
    }
  }

  /** Flashlight or headlamp on and off (the best one you own). */
  private toggleLamp(): void {
    const s = this.store;
    const kind: LampKind | null = s.has('headlamp') ? 'headlamp' : s.has('flashlight') ? 'flashlight' : null;
    if (!kind) {
      s.toast(
        "No flashlight. The Gas-N-Grub sells a Mag-Lite and a coon-huntin' headlamp.",
        'info',
        ':flashlight:',
      );
      return;
    }
    this.lamp.set(this.lamp.on ? null : kind, this.player.rig);
    this.sfx('lever');
  }

  /** The beam goes where you look in first person, and where you're facing otherwise. */
  private updateLamp(): void {
    if (this.lamp.on && !this.store.has(this.lamp.on)) this.lamp.set(null, this.player.rig);
    if (!this.lamp.on) {
      this.lamp.update(this.eye, this.lampDir, 0, false);
      return;
    }
    const fp = this.cameraRig.firstPerson || this.stages.playing;
    const from = this.lampFrom;
    if (fp) {
      from.copy(this.camera.position);
      this.camera.getWorldDirection(this.lampDir);
      from.y -= 0.12;
    } else {
      this.player.eye(from);
      from.y += 0.08;
      const f = this.player.facing;
      this.lampDir.set(Math.sin(f), -0.22, Math.cos(f)).normalize();
    }
    const dark = this.interiors.inside ? 0.6 : this.sky.darkness;
    this.lamp.update(from, this.lampDir, dark, !fp && !this.driving.active);
  }

  /** Scroll all the way in to see through your own eyes; scroll back out to leave. */
  private zoomIntoFirstPerson(): void {
    const rig = this.cameraRig;
    const w = this.input.wheel;
    if (!w) return;
    if (rig.firstPerson ? w > 0 : w < 0 && rig.distance <= rig.minDistance + 0.01) {
      this.toggleFirstPerson();
      this.input.wheel = 0;
    }
  }

  /** The Back Forty closes in around you: fog, gloom and frogs. */
  private updateMurk(dt: number): void {
    const p = this.player.position;
    const depth = backFortyDepth(p.x, p.z);
    this.murk = damp(this.murk, depth, 1.4, dt);
    if (depth > 0.6 && this.time - this.backFortySeen > 120)
      this.store.toast('The Back Forty. Lock your car. Then lock it again.', 'meme', ':pin:');
    if (depth > 0.2) this.backFortySeen = this.time;
  }

  private updateMovement(dt: number): void {
    const s = this.store;
    if (this.stunts.active) {
      const press = this.input.wasPressed('Space') || this.input.wasPressed('KeyE');
      this.stunts.update(dt, press, this.decor.trampolineMat);
      return;
    }
    if (this.driving.active) {
      this.driving.update(dt);
      return;
    }
    if (this.rascal.riding && this.rascal.drive(dt)) {
      this.checkTraffic();
      return;
    }
    const axis = this.input.moveAxis();
    const f = this.cameraRig.forward(this.tmp);
    const r = this.cameraRig.right(new THREE.Vector3());
    const sprint = this.input.isDown('ShiftLeft') || this.input.isDown('ShiftRight');
    this.player.update(
      dt,
      this.time,
      {
        dx: f.x * axis.y + r.x * axis.x,
        dz: f.z * axis.y + r.z * axis.x,
        sprint,
        jump: this.input.wasPressed('Space'),
      },
      this.world.collision,
      { buzz: s.stat('buzz'), energy: s.stat('energy') },
    );
    // In first person your body turns with your head.
    if (this.cameraRig.firstPerson && !this.player.busy) this.player.facing = this.cameraRig.yaw + Math.PI;
    if (this.player.sprinting) {
      s.addStat('energy', -0.5 * dt);
      if (!s.state.counters.sprints) {
        s.incr('sprints');
        s.toast('CROCS IN SPORT MODE ACTIVATED', 'meme', ':gator:');
      }
    }
    this.checkTraffic();
  }

  private collectPickups(): void {
    const reach = this.driving.active ? 2 : 1; // also while driving: run 'em over
    for (const kind of this.pickups.update(
      this.time,
      this.player.position.x,
      this.player.position.z,
      reach,
    )) {
      this.sfx('pickup');
      if (kind === 'can') {
        this.store.give('cans', 1);
        this.hud.float('+1 :empty-can:', this.player.headPosition(), '#d9dde0');
      } else {
        this.store.give('scrap', 1);
        this.hud.float('+1 :scrap:', this.player.headPosition(), '#b8c4cc');
      }
    }
  }

  private updatePrompt(blocked: boolean): void {
    if (blocked || this.stunts.active || this.player.busy) {
      this.hud.setPrompt(null);
      this.showMarkers(false);
      return;
    }
    if (this.driving.active) {
      this.interactions.current = null;
      this.hud.setPrompt(Math.abs(this.driving.car.speed) < 3 ? 'Get out' : null);
      this.showMarkers(false);
      return;
    }
    this.interactions.update(this.player.position.x, this.player.position.z);
    this.hud.setPrompt(this.interactions.current ? this.interactions.currentPrompt : null);
    this.showMarkers(true);
  }

  /**
   * Makes it obvious what you can use: the thing E would use glows, and indoors every
   * usable thing nearby gets a badge. Outdoors only the current one does.
   */
  private showMarkers(on: boolean): void {
    const cur = on ? this.interactions.current : null;
    this.highlighter.set(cur?.highlight?.() ?? null);
    if (!on) {
      this.hud.setMarkers([]);
      return;
    }
    const p = this.player.position;
    const inside = this.interiors.inside;
    const range = inside ? 7.5 : 0;
    const near = inside ? this.interactions.nearby(p.x, p.z, range) : [];
    if (cur && !near.some((n) => n.item === cur)) {
      const at = cur.at();
      near.push({ item: cur, prompt: this.interactions.currentPrompt, x: at.x, z: at.z, distance: 0 });
    }
    const list = [];
    for (const n of near) {
      const icon = n.item.icon ?? (n.item === cur ? ':high-five:' : null);
      if (!icon) continue;
      const top = this.markerAnchor(n.item.highlight?.() ?? null);
      // In first person a badge on the spot you're standing on would sit in your lap.
      if (!top && this.cameraRig.firstPerson && n.distance < 1.5) continue;
      list.push({
        id: n.item.id,
        icon,
        x: top?.x ?? n.x,
        y: top?.y ?? 1.55,
        z: top?.z ?? n.z,
        current: n.item === cur,
        far: inside ? n.distance / range : 0,
      });
    }
    this.hud.setMarkers(list);
  }

  /** A badge floats just over the top of the thing it's for. */
  private markerAnchor(obj: THREE.Object3D | null): THREE.Vector3 | null {
    if (!obj) return null;
    let at = this.markerTops.get(obj);
    if (!at) {
      const box = new THREE.Box3().setFromObject(obj);
      at = box.getCenter(new THREE.Vector3());
      at.y = Math.min(2.2, box.max.y + 0.3);
      this.markerTops.set(obj, at);
    }
    return at;
  }

  private handleHotkeys(): void {
    const i = this.input;
    if (this.time < this.suppressUntil) return;
    if (i.wasPressed('KeyP') || i.wasPressed('Tab') || i.wasPressed('Escape')) {
      this.phone.show(i.wasPressed('Escape') ? 'settings' : undefined);
      return;
    }
    if (i.wasPressed('KeyC')) {
      this.phone.openChat();
      return;
    }
    if (i.wasPressed('KeyM')) this.toggleMusic();
    if (i.wasPressed('KeyV')) this.toggleFirstPerson();
    if (i.wasPressed('KeyL')) this.toggleLamp();
    if (this.stunts.active) return;
    if (i.wasPressed('KeyB') && !this.driving.active) this.rascal.toggle();
    const a = this.activities;
    if (i.wasPressed('KeyE')) {
      if (this.driving.active) this.driving.stop();
      else if (a.canAct()) this.interactions.trigger();
    }
    if (i.wasPressed('KeyQ')) a.drinkBeer();
    if (i.wasPressed('KeyF')) a.eat();
    if (i.wasPressed('KeyR')) a.drinkDewd();
    if (i.wasPressed('KeyT')) a.scratchTicket();
    if (i.wasPressed('KeyG')) a.lightFireworks();
  }

  private checkVitals(): void {
    const s = this.store;
    if (this.rest.sleeping || this.blocked) return;
    if (s.stat('health') <= 0) this.rest.emergencyRoom();
    else if (isPassOutTime(s.minutes)) this.rest.passOut('late');
    else if (s.stat('energy') <= 0) this.rest.passOut('exhausted');
  }

  /** Pedestrians vs. Highway 41. */
  private checkTraffic(): void {
    if (this.trafficCooldown > 0) return;
    const p = this.player.position;
    const hit = this.effects.trafficHit(p.x, p.z, 0.4);
    if (!hit) return;
    this.trafficCooldown = 2;
    const semi = hit.kind === 'semi';
    this.player.teleport(p.x + hit.dir * (semi ? 5 : 2), p.z < 61 ? 54.5 : 67.5);
    this.sfx(semi ? 'semiHorn' : 'horn');
    this.sfx('thud');
    this.effects.shake(semi ? 1.2 : 0.6, semi ? 0.8 : 0.4);
    this.store.apply({ stats: { health: semi ? -60 : -25 } });
    this.store.incr(semi ? 'semiHits' : 'trafficHits');
    this.store.toast(
      semi
        ? 'An 18-wheeler sent you into next week. The driver never even tapped the brakes.'
        : 'You got clipped by a pickup. Look both ways, hero.',
      'bad',
      semi ? ':semi:' : ':lifted-truck:',
    );
  }

  private updateChase(): void {
    const until = Number(this.store.flag('chase_until') ?? 0);
    if (!this.effects.chaseTarget || until <= 0) return;
    if (this.store.now > until) {
      this.effects.endChase();
      this.store.setFlag('chase_until', 0);
      this.store.toast("Peanut found his own way home. He's fine. He's always fine.", 'info', ':dog:');
    }
  }

  private caughtPeanut(): void {
    this.effects.endChase();
    this.store.setFlag('chase_until', 0);
    this.sfx('yip');
    this.modal.show({
      title: 'Got Him!',
      speaker: { id: 'darlene', name: 'Darlene', voice: 330 },
      text: "'MY BABY! Peanut, you little terrorist!' Peanut bites you a little bit. Darlene presses a twenty and a five into your hand. 'You're alright, sugar.'",
      choices: [],
      leave: 'Anytime',
    });
    this.store.apply({ money: 25, clout: 4 });
  }

  /** NPCs within earshot occasionally say something. */
  private updateBarks(dt: number): void {
    this.barkTimer -= dt;
    for (const a of this.npcs.agents)
      this.hud.moveBubble(a.def.id, this.tmp.copy(a.position).setY(2.35 * a.def.look.height));
    if (this.barkTimer > 0 || this.blocked || this.mode !== 'play') return;
    this.barkTimer = this.rng.range(7, 12);
    const near = this.npcs.agents.filter(
      (a) => a.active && a.state !== 'talk' && a.position.distanceTo(this.player.position) < 16,
    );
    if (!near.length) return;
    const a = this.rng.pick(near);
    // Putter past on the Rascal and everybody's got an opinion.
    const line =
      this.rascal.riding && this.rng.chance(0.6)
        ? this.rng.pick(RASCAL_BARKS)
        : this.rng.pick(a.def.lines(this.ctx));
    const short = line.length > 70 ? `${line.slice(0, 67)}…` : line;
    this.hud.bubble(a.def.id, short, this.tmp.copy(a.position).setY(2.35), 4);
  }

  /** What the objective pill should point at right now. */
  private objective(): { text: string; target: THREE.Vector3 | null } | null {
    const s = this.store;
    const chase = this.effects.chaseTarget;
    if (chase) return { text: ':dog: Catch Peanut! (Sprint in Sport Mode)', target: chase };
    const job = this.jobs.objective();
    if (job) return job;
    const favor = this.favors.objective();
    if (favor) return favor;
    const delivery = this.deliveries.objective();
    if (delivery) return delivery;
    const shine = this.shineRuns.objective();
    if (shine) return shine;
    const rascal = this.rascal.objective();
    if (rascal) return rascal;
    const pinned = this.hustles.objective();
    if (pinned) return pinned;
    const mud = this.fairgrounds.runText;
    if (mud) return { text: mud, target: v3(MUD_BOG.maxX - 1.5, (MUD_BOG.minZ + MUD_BOG.maxZ) / 2) };
    if (this.driving.active) return null;
    const c = s.state.counters;
    if (!c.beers)
      return {
        text: isTouch()
          ? 'Crack a cold one: tap :beer:, or sit on your porch couch'
          : 'Crack a cold one on your porch couch (E) or press Q',
        target: v3(this.localHome.spots.couch.x, this.localHome.spots.couch.z),
      };
    if (!c.cansRecycled && !c.scrapSold) {
      return {
        text: "Grab sparkly cans & scrap, sell them at Junkyard Jerry's",
        target: v3(SPOTS.junkyardWindow.x, SPOTS.junkyardWindow.z),
      };
    }
    if (!c.jobs && !s.state.job && s.day <= 3)
      return {
        text: 'Check the odd-job board at Suds-N-Duds',
        target: v3(SPOTS.jobBoard.x, SPOTS.jobBoard.z),
      };
    const r = s.rentStatus();
    if (!r.paid && r.daysUntil <= 1)
      return { text: `Pay $${r.amount} lot rent at the office`, target: v3(SPOTS.office.x, SPOTS.office.z) };
    const lot10 = this.landlord.objective();
    if (lot10) return lot10;
    if (s.day >= 2 && !c.noodles && !c.copperLbs && !c.shineJars)
      return {
        text: 'Explore the Back Forty: catfish, copper & moonshine money',
        target: v3(SPOTS.backFortySign.x, SPOTS.backFortySign.z),
      };
    if (!s.flag('won') && s.canAfford(CONFIG.doubleWidePrice)) {
      return {
        text: 'You can afford the DOUBLE WIDE! Go see Big Dave',
        target: v3(SPOTS.bigDave.x, SPOTS.bigDave.z),
      };
    }
    // The next goal: after your double wide, or right away when you're in a park with others.
    if (
      !s.flag('park_owned') &&
      (s.flag('double_wide') || s.flag('won') || this.neighbors.fundContributions().length)
    ) {
      const f = this.fundView();
      return {
        text: `Buy back the park from Apex: $${Math.round(f.total).toLocaleString()} / $${f.goal.toLocaleString()} (office)`,
        target: v3(SPOTS.office.x, SPOTS.office.z),
      };
    }
    return null;
  }

  /** Ambience beds and the soundtrack follow where you are, the hour and the weather. */
  private updateSound(dt: number): void {
    const at = this.driving.active ? this.driving.position : this.player.position;
    this.audio.update(dt, {
      mode: this.mode === 'title' ? 'title' : this.end.isOpen ? 'end' : 'play',
      bar: this.interiors.current === this.strip.bar,
      fair: inFairgrounds(at.x, at.z),
      murk: this.murk,
      night: this.sky.darkness,
      storm: this.sky.storm,
      indoors: this.interiors.inside,
    });
  }

  private render(dt: number): void {
    // Notifications pop up under your feet (mid-screen in first person, and off to the side
    // while a menu, a shop, the phone or a mini-game has the screen).
    this.hud.toastAnchor = 'center';
    if (this.mode === 'play' && this.stages.playing) {
      this.hud.toastAnchor = 'side';
      this.stages.frame(this.camera, dt);
      this.interiors.update(dt, this.camera);
      this.sky.indoors = this.interiors.inside ? 1 : 0;
      this.sky.update(this.store.minutes, dt, this.player.position, this.camera);
    } else if (this.mode === 'play') {
      const focus =
        this.stuntFlow.focus ?? (this.driving.active ? this.driving.position : this.player.position);
      const drunk = clamp((this.store.stat('buzz') - 60) / 40, 0, 1);
      // First person needs a body to look out of; a backflip on the trampoline doesn't count.
      const fp = this.cameraRig.firstPerson && !this.stuntFlow.focus && !this.stunts.active;
      const eye = fp
        ? this.driving.active
          ? this.driving.eye(this.eye)
          : this.player.eye(this.eye)
        : undefined;
      this.player.viewHidden = fp;
      this.player.rig.root.visible = !fp && !this.player.hidden;
      if (!fp) this.hud.toastAnchor = this.toastAt.copy(focus).setY(0);
      if (this.blocked) this.hud.toastAnchor = 'side';
      this.cameraRig.update(dt, this.tmp.copy(focus).setY(this.stuntFlow.focus ? 1 : this.player.y * 0.5), {
        eye,
        height: this.driving.active ? this.driving.car.stats.seat : this.rascal.riding ? 1.3 : 1.5,
        sway: this.settings.sway ? drunk : 0,
        shake: this.effects.shakeOffset,
        followYaw:
          this.driving.active && Math.abs(this.driving.car.speed) > 1
            ? this.driving.car.heading - Math.PI / 2
            : fp
              ? undefined
              : this.rascal.followYaw,
        time: this.time,
      });
      this.interiors.update(dt, this.camera);
      this.sky.murk = this.murk;
      this.sky.indoors = this.interiors.inside ? 1 : 0;
      this.renderer.toneMappingExposure = 1.05 * (1 - 0.12 * this.murk);
      this.sky.update(this.store.minutes, dt, focus, this.camera);
    }
    this.updateSound(dt);
    this.world.update(this.time, dt, this.sky.darkness);
    this.decor.update(this.time, dt, this.sky.darkness);
    this.highlighter.update(this.time);
    if (this.mode === 'play') {
      this.updateLamp();
      this.abduction.ufo.update(this.time);
    }
    this.hud.updateLabels(dt, this.camera, window.innerWidth, window.innerHeight);
    this.renderer.render(this.scene, this.camera);
    if (this.viewRoot.children.length) {
      this.viewRoot.position.copy(this.camera.position);
      this.viewRoot.quaternion.copy(this.camera.quaternion);
      this.renderer.autoClear = false;
      this.renderer.clearDepth();
      this.renderer.render(this.viewScene, this.camera);
      this.renderer.autoClear = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Debug hooks (exposed on window.__game in dev builds only)
  // ---------------------------------------------------------------------------

  readonly debug = {
    /** Advance the simulation by `frames` fixed steps (works even when the tab is hidden). */
    step: (frames = 60, dt = 1 / 60): void => {
      for (let i = 0; i < frames; i++) this.tick(dt);
    },
    money: (n: number): void => this.store.earn(n),
    event: (id: string): boolean => this.director.trigger(id),
    time: (h: number): void => {
      this.store.state.time.minutes = h * 60;
    },
    give: (id: ItemId, n: number): void => this.store.give(id, n),
    decor: (id: string): void => this.store.addDecor(id),
    teleport: (spot: SpotId): void => this.teleportTo(spot),
    /** They're coming. */
    ufo: (): void => this.abduction.begin(),
    /** Render the current view at an exact pixel size (used to make the social preview image). */
    capture: (width: number, height: number): HTMLCanvasElement => {
      const size = this.renderer.getSize(new THREE.Vector2());
      const ratio = this.renderer.getPixelRatio();
      this.renderer.setPixelRatio(1);
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.world.setParticleScale(height);
      this.renderer.render(this.scene, this.camera);
      const out = document.createElement('canvas');
      out.width = width;
      out.height = height;
      out.getContext('2d')?.drawImage(this.renderer.domElement, 0, 0);
      this.renderer.setPixelRatio(ratio);
      this.renderer.setSize(size.x, size.y, false);
      this.applyViewport();
      return out;
    },
    /** A large portrait of the current player character (used for app icons). */
    portrait: (size = 512): string => renderPortrait(this.renderer, this.store.state.player.look, size),
    key: (code: string, down: boolean): void => {
      window.dispatchEvent(new KeyboardEvent(down ? 'keydown' : 'keyup', { code, key: code }));
    },
    tap: (code: string): void => {
      this.debug.key(code, true);
      this.debug.step(1);
      this.debug.key(code, false);
    },
    /** Walk (through the real input path) toward a point; returns where we ended up. */
    goto: (x: number, z: number, sprint = false, maxFrames = 1500) => {
      this.debug.key('KeyW', true);
      if (sprint) this.debug.key('ShiftLeft', true);
      let f = 0;
      for (; f < maxFrames; f += 2) {
        const dx = x - this.player.position.x;
        const dz = z - this.player.position.z;
        if (this.blocked || Math.hypot(dx, dz) < 0.5) break;
        this.cameraRig.yaw = Math.atan2(-dx, -dz);
        this.debug.step(2);
      }
      this.debug.key('KeyW', false);
      this.debug.key('ShiftLeft', false);
      this.debug.step(8);
      return {
        frames: f,
        x: this.player.position.x,
        z: this.player.position.z,
        prompt: this.interactions.currentPrompt,
      };
    },
  };
}
